import React from "react";
import { Shield, Lock, Globe, Key, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SECURITY_FEATURES = [
  {
    icon: Lock,
    title: "Encrypted in transit and at rest",
    description: "TLS 1.3 for data in transit, AES-256 encryption at rest",
  },
  {
    icon: Key,
    title: "Short, configurable data retention windows",
    description: "Default 30-day retention, customizable to your policy",
  },
  {
    icon: Globe,
    title: "Global compliance: GDPR, CCPA, and India DPDP ready",
    description: "Built-in compliance features for all major regulations",
  },
];

export default function SecuritySection() {
    const navigate = useNavigate()
    const handleView = () => {
        navigate("/company");
    }
  return (
    <section className="py-16 lg:py-24 bg-white dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 mb-6">
              <Shield className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                Enterprise-grade security
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-6">
              Security-first,{" "}
              <span className="text-sky-600 dark:text-sky-400">
                privacy by design
              </span>
            </h2>

            <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
              Your data security isn't an afterthought. VALIDMAIL is built from
              the ground up with enterprise-grade security and privacy
              protection.
            </p>

            <div className="space-y-6 mb-8">
              {SECURITY_FEATURES.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-sky-300 dark:hover:border-sky-700 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
            onClick={handleView}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white dark:border-sky-500 dark:text-sky-400 dark:hover:bg-sky-500 dark:hover:text-white transition-all duration-300 shadow-md hover:shadow-xl group">
              View security & compliance
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 rounded-3xl p-8 border-2 border-slate-200 dark:border-slate-700 shadow-2xl">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        SOC 2 Type II
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Compliance
                      </div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                </div>

                <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        GDPR Ready
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        EU Data Protection
                      </div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                </div>

                <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-500 flex items-center justify-center">
                      <Lock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        India DPDP
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Data Protection
                      </div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                </div>

                <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sky-500 flex items-center justify-center">
                      <Key className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        AES-256 Encryption
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Data at Rest
                      </div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
                </div>
              </div>
            </div>

            <div className="absolute -z-10 -top-4 -right-4 w-72 h-72 bg-sky-200/30 dark:bg-sky-900/30 rounded-full blur-3xl" />
            <div className="absolute -z-10 -bottom-4 -left-4 w-72 h-72 bg-blue-200/30 dark:bg-blue-900/30 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
}
